import moment from 'moment'
import * as tz from 'moment-timezone'

import { SERVER_TIMEZONE, SERVER_TIME_FORMAT, SERVER_DATETIME_FORMAT } from './const'

/** A helper function to get values from deeply nested objects or arrays.
* The function will return an 'undefined' if the desired value or a value
* in the path is either undefined or not existent.
*
* obj {Object} The object to get the value from
* path {String} The path to the desired value inside the obj
*    const obj = {
      level1: {
        level2: {
          level3: [
            'a', 'b', 'c'
          ]
        }
      }
    }
* Example: deepGet(obj, 'level1.level2.level3') will output ['a', 'b', 'c']
* Example: deepGet(obj, 'level1.level2.level3[1]') will output 'b'
* Example: deepGet(obj, 'level1.level2.level3.1') will output 'b'
* Example: deepGet(obj, 'level1.level2.level3[4]') will output undefined
*/
export function deepGet(obj, path) {
  return path.replace('[', '.').replace(']', '').split('.')
    .reduce((memo, key) => (
      memo && memo[key]
    ), obj)
}

/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 */
export function isObject(item) {
  return (item && typeof item === 'object' && !Array.isArray(item))
}

// https://stackoverflow.com/questions/27936772/how-to-deep-merge-instead-of-shallow-merge
export function mergeDeep(target, ...sources) {
  if (!sources.length) return target
  const source = sources.shift()

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} })
        mergeDeep(target[key], source[key])
      } else {
        Object.assign(target, { [key]: source[key] })
      }
    }
  }

  return mergeDeep(target, ...sources)
}

let timeout = null
export function debounce(func, time) {
  if (timeout) clearTimeout(timeout)
  timeout = setTimeout(func, time)
}

export const roleList = {
  'admin': 1,
  'account': 2
}

/**
* Verifies the role of the current user
*/
export function hasRole(roleName, store) {
  const roleId = roleList[roleName]
  const userRoles = store.getters['auth/user'].roles
  if (!userRoles) {
    return false
  }
  return !!userRoles.data.find(el => el.id === roleId)
}

/* default add to collection */
export function addToCollection(self, collection, itemData, Model) {
  const alreadyExistingModel = collection.find(obj => obj.id === itemData.id)
  if (alreadyExistingModel) {
    if (typeof alreadyExistingModel.set === 'function') {
      alreadyExistingModel.set(itemData)
    }
    else {
      Object.assign(alreadyExistingModel, itemData)
    }
  }

  else {

    let ItemModel = itemData

    // build the model
    if (Model && !(ItemModel instanceof Model)) {
      ItemModel = new Model(itemData, self)
    }

    collection.splice(collection.length, 0, ItemModel)
  }
}

/**
 * @param {Date/Moment/String} serverDate - expects a date of sorts
 * moment object
 * date object
 * 'YYYY-MM-DD HH:mm:ss' string that our server stores (assumed to be a server date)
 * @return {Moment} - because of timezones, it's necessary to return the whole object
 */
export function convertDateToUserLocal(serverDate, opts = {}) {

  if (!serverDate) {
    console.warn('attempting to convert a date but none given')
    return null
  }
  if (typeof serverDate === 'string') {
    if (opts.type === 'time') {
      serverDate = moment('2020-01-01 ' + serverDate, SERVER_DATETIME_FORMAT)
    }
    serverDate = tz.tz(serverDate, SERVER_TIMEZONE)
  }
  if (!moment.isMoment(serverDate)) {
    serverDate = moment(serverDate)
  }

  const userTZ = tz.tz.guess()
  const userDate = serverDate.clone().tz(userTZ)

  // barDisplay is localized, so for DE: "31.07.2017 | 15:00"
  if (opts.barDisplay) {
    return `${userDate.format('L')} | ${userDate.format('LT')}`
  }
  // comma display is what you see below (no style localization, like AM/PM)
  if (opts.commaDisplay) {
    return `${userDate.format('DD.MM.YYYY, LT')}`
  }
  // Time display doesn't understand timezone info, so manually adjust it
  if (opts.type === 'time') {
    const offset = userDate.utcOffset()
    userDate.add(offset, 'minutes')
  }
  return userDate
}

/**
 * @param {Date/Moment/String} serverDate - expects a date of sorts
 * moment object
 * date object
 * 'YYYY-MM-DD HH:mm:ss' string that our server stores (assumed to be a user date)
 * @param {Object} opts
 * @param {Bool} opts.simplified - returns in format for server
 * @return {Moment} - because of timezones, it's necessary to return the whole object
 */
export function convertDateToSystemLocal(localDate, opts = {}) {

  if (!localDate) {
    console.warn('attempting to convert a date but none given')
    return null
  }
  if (typeof localDate === 'string') {
    // use userTZ if strings are allowed to come in as user time
    // use SERVER_TIMEZONE if all strings are assumed server strings
    const userTZ = tz.tz.guess()
    localDate = moment.tz(localDate, userTZ)
  }
  if (!moment.isMoment(localDate)) {
    localDate = moment(localDate)
  }
  const serverDate = localDate.tz(SERVER_TIMEZONE)

  if (opts.simplified) {
    return serverDate.format(SERVER_DATETIME_FORMAT)
  }
  if (opts.simplifiedTime) {
    return serverDate.format(SERVER_TIME_FORMAT)
  }
  return serverDate
}

// Adds dot format like 1200 -> 1.200
export function convertNumberToLocale(value, locale) {
  if (value === 0) {
    return 0
  }

  if (value) {
    return value.toLocaleString(locale)
  }

  return null
}



// WEBPACK FOOTER //
// ./src/components/shared/helper.js